"use client";

import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronUp } from "lucide-react";
import { cn } from "../../utils/utils";
import { Checkbox } from "./Checkbox";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
    <AccordionPrimitive.Item
        ref={ref}
        className={cn("", className)}
        {...props}
    />
));
AccordionItem.displayName = "AccordionItem";

// Interface for additional checkbox props
interface AccordionTriggerProps
    extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
    checkboxProps?: {
        checked?: boolean;
        onCheckedChange?: (checked: boolean) => void;
        disabled?: boolean;
    };
    showCheckbox?: boolean;
}

const AccordionTrigger = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Trigger>,
    AccordionTriggerProps
>(
    (
        { className, children, checkboxProps, showCheckbox = true, ...props },
        ref
    ) => (
        <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
                ref={ref}
                className={cn(
                    "flex flex-1 items-center gap-2 py-4 font-bold text-[#535353] dark:text-[#D9D9D9] text-[14px] transition-all [&[data-state=closed]>svg]:rotate-90 [&[data-state=open]>svg]:rotate-[180deg]",
                    className
                )}
                {...props}
            >
                <ChevronUp
                    className="h-4 w-4 shrink-0 transition-transform duration-200 stroke-[#D9D9D9]"
                    strokeWidth={2.5}
                />
                {
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            if (checkboxProps?.onCheckedChange) {
                                checkboxProps.onCheckedChange(
                                    !checkboxProps.checked
                                );
                            }
                        }}
                    >
                        {checkboxProps?.checked ? (
                            <Checkbox
                                asChild
                                {...checkboxProps}
                                checked={true}
                            />
                        ) : (
                            <span
                                ref={ref}
                                className={cn(
                                    "flex items-center justify-center h-[16px] w-[16px] rounded-[4px] border border-[#C6C5C5] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-[#C6D92E] dark:data-[state=checked]:bg-[#006A66] data-[state=checked]:border-none p-0"
                                )}
                                {...props}
                            ></span>
                        )}
                    </div>
                }
                {children}
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
    )
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Content
        ref={ref}
        className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
        {...props}
    >
        <div className={cn("pb-4 pt-0", className)}>{children}</div>
    </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
